/**
 * Typed-answer card (HTML over the canvas so the phone keyboard works), shared by every
 * "reveal & guess" game. Suggestions come from a picture pool (type-ahead); the game decides
 * what a correct/wrong answer does via onSubmit. Styles: src/kit/page.css (.guess*).
 *
 *   const close = openGuess(scene, {
 *     question: "Bu ne?", pool, paint: (ctx, size) => drawRevealed(ctx, size),
 *     onSubmit: (text) => ({ correct, message, finished }),
 *     onClose: () => {},
 *   });
 */
import Phaser from "phaser";
import { suggestAnswers, type Picture } from "@enjoyment/core";
import { W } from "./ui";
import { currentLang, pick, type Lang } from "./lang";

const STRINGS = {
  close: { tr: "Kapat", en: "Close" },
  placeholder: { tr: "Cevabı yaz…", en: "Type your answer…" },
  tried: { tr: "Denenenler", en: "Tried" },
} as const;

export interface GuessResult {
  correct: boolean;
  /** Shown under the input (e.g. "Yanlış · −2 hamle"). */
  message: string;
  /** Round over (correct or out of tries): the card closes after a short beat. */
  finished: boolean;
}

export interface GuessOptions {
  question: string;
  pool: Picture[];
  lang?: string;
  /** Optional: paint what the player has revealed so far into a square canvas. */
  paint?: (ctx: CanvasRenderingContext2D, size: number) => void;
  hint?: string;
  tried?: () => string[];
  onSubmit: (text: string) => GuessResult;
  onClose?: () => void;
}

export function openGuess(scene: Phaser.Scene, opts: GuessOptions): () => void {
  const H = scene.scale.height;
  // The card's own texts follow the suggestions' language (opts.lang), else the player's.
  const lang: Lang = opts.lang === "tr" || opts.lang === "en" ? opts.lang : currentLang();
  const s = (key: keyof typeof STRINGS) => pick(lang, STRINGS[key]);
  const dim = scene.add.rectangle(W / 2, H / 2, W * 3, H, 0x000000, 0.6).setInteractive().setDepth(2000);
  dim.on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => ev.stopPropagation());

  const root = document.createElement("div");
  root.className = "guess";
  (root.style as CSSStyleDeclaration & { zoom?: string }).zoom = String(Math.min(2, Math.max(1, window.innerWidth / 540)));
  root.innerHTML = `
    <div class="guess-card">
      ${opts.paint ? '<canvas class="guess-pic" width="288" height="288"></canvas>' : ""}
      <div class="guess-q"></div>
      <input class="guess-input" type="text" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" enterkeyhint="go" />
      <div class="guess-list"></div>
      <div class="guess-feedback"></div>
      <div class="guess-wrong"></div>
      <button type="button" class="guess-close"></button>
    </div>`;
  const $ = <T extends Element>(s: string) => root.querySelector<T>(s)!;
  const input = $<HTMLInputElement>(".guess-input");
  const list = $<HTMLDivElement>(".guess-list");
  const feedback = $<HTMLDivElement>(".guess-feedback");
  const wrongEl = $<HTMLDivElement>(".guess-wrong");
  $<HTMLDivElement>(".guess-q").textContent = opts.question;
  input.placeholder = s("placeholder");
  $<HTMLButtonElement>(".guess-close").textContent = s("close");
  feedback.textContent = opts.hint ?? "";
  if (opts.paint) {
    const c = $<HTMLCanvasElement>(".guess-pic").getContext("2d");
    if (c) opts.paint(c, 288);
  }
  const renderTried = () => {
    const t = opts.tried?.() ?? [];
    wrongEl.textContent = t.length ? `${s("tried")}: ${t.join(", ")}` : "";
  };
  renderTried();

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    root.remove();
    dim.destroy();
    opts.onClose?.();
  };
  let current: { label: string }[] = [];
  const submit = (text: string) => {
    const v = text.trim();
    if (!v || closed) return;
    const r = opts.onSubmit(v);
    feedback.textContent = r.message;
    root.classList.remove("is-wrong", "is-correct");
    void root.offsetWidth;
    root.classList.add(r.correct ? "is-correct" : "is-wrong");
    renderTried();
    input.value = "";
    renderList();
    if (r.finished) {
      input.blur();
      setTimeout(close, r.correct ? 350 : 700);
    } else input.focus();
  };
  const renderList = () => {
    current = suggestAnswers(opts.pool, input.value, opts.lang ?? "tr", 5);
    list.innerHTML = "";
    for (const s of current) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "guess-opt";
      b.textContent = s.label;
      b.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        submit(s.label);
      });
      list.appendChild(b);
    }
  };
  input.addEventListener("input", renderList);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit(current[0]?.label ?? input.value);
    } else if (e.key === "Escape") close();
  });
  $<HTMLButtonElement>(".guess-close").addEventListener("pointerdown", (e) => {
    e.preventDefault();
    close();
  });
  root.addEventListener("pointerdown", (e) => {
    if (e.target === root) close();
  });
  document.body.appendChild(root);
  scene.events.once("shutdown", close);
  setTimeout(() => input.focus(), 50);
  return close;
}

/** Paint a pack picture into a canvas, only the blocks marked revealed (grid of bw×bh blocks). */
export function paintPicture(
  ctx: CanvasRenderingContext2D,
  size: number,
  picture: Picture,
  pixels: number[],
  revealed: boolean[],
  blocksW: number,
  blocksH: number,
): void {
  const px = size / picture.size;
  const bw = picture.size / blocksW;
  const bh = picture.size / blocksH;
  ctx.fillStyle = "#1b1f3a";
  ctx.fillRect(0, 0, size, size);
  for (let r = 0; r < blocksH; r++)
    for (let c = 0; c < blocksW; c++) {
      if (!revealed[r * blocksW + c]) {
        ctx.fillStyle = "#30365f";
        ctx.fillRect(c * bw * px + 1, r * bh * px + 1, bw * px - 2, bh * px - 2);
        continue;
      }
      for (let y = Math.floor(r * bh); y < Math.floor((r + 1) * bh); y++)
        for (let x = Math.floor(c * bw); x < Math.floor((c + 1) * bw); x++) {
          const col = pixels[y * picture.size + x]!;
          if (col < 0) continue;
          ctx.fillStyle = picture.palette[col]!;
          ctx.fillRect(x * px, y * px, px + 0.5, px + 0.5);
        }
    }
}
